import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import queryKeys from '../../support/uiStateKeys';
import acquisitionTypes, {
  type AcquisitionType,
} from '../../support/acquisitionTypes';
import { useAcquisitionType } from '../../hooks/stateful/useAcquisitionType';
import { type SubscriptionOffer } from '../../../shared/models/subscriptionOffer';

export type SubscriptionOption =
  | 'single'
  | 'subscription'
  | 'ultrapack'
  | 'customlicense';

const dataKey = queryKeys.SELECTED_SUBSCRIPTION_OPTION;
export const defaultSubscriptionOption: SubscriptionOption = 'subscription';
export const fallbackSubscriptionOption: SubscriptionOption = 'ultrapack';

export const resolveDefaultSubscriptionOption = (
  subscriptionOfferKind: SubscriptionOffer['kind'],
  acquisitionType: AcquisitionType
): SubscriptionOption =>
  subscriptionOfferKind !== 'subscription' ||
  acquisitionType === acquisitionTypes.ALACARTE_LICENSE
    ? fallbackSubscriptionOption
    : defaultSubscriptionOption;

const useSelectedSubscriptionOption = () => {
  const queryClient = useQueryClient();
  const { acquisitionType, subscriptionOffer } = useAcquisitionType();

  const computedDefault = resolveDefaultSubscriptionOption(
    subscriptionOffer.kind,
    acquisitionType
  );

  const { data } = useSuspenseQuery<SubscriptionOption>({
    queryKey: [dataKey],
    queryFn: ({ queryKey }) =>
      queryClient.getQueryData(queryKey) ?? computedDefault,
    initialData: computedDefault,
  });

  const setSelectedSubscriptionOption = (option: SubscriptionOption) => {
    queryClient.setQueryData([dataKey], option);
  };

  return {
    selectedSubscriptionOption: data,
    setSelectedSubscriptionOption,
  };
};

export default useSelectedSubscriptionOption;
